import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, Building2, Loader2 } from "lucide-react";
import MainLayout from "@/components/layouts/MainLayout";

interface CompanyConfig {
  id: string;
  company_name: string;
  company_address?: string;
  company_city?: string;
  company_state?: string;
  company_country?: string;
  company_email?: string;
  company_website?: string;
  company_phone?: string;
  company_logo_url?: string;

  currency?: string;
  rnc?: string;
}

const CompanyConfig = () => {
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const lightLogoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLight, setIsUploadingLight] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_country: 'República Dominicana',
    company_email: '',
    company_website: '',
    company_phone: '',
    currency: 'DOP',
    rnc: '',
  });

  // Consulta para obtener la configuración actual
  const { data: config, isLoading, error: queryError } = useQuery({
    queryKey: ['company-config'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('company_config')
          .select('*')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error de base de datos:', error);
          throw error;
        }

        return data as CompanyConfig | null;
      } catch (error) {
        console.error('Error en la consulta:', error);
        throw error;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  // Efecto para actualizar formData cuando se carga la configuración
  React.useEffect(() => {
    if (config) {
      setFormData({
        company_name: config.company_name || '',
        company_address: config.company_address || '',
        company_city: config.company_city || '',
        company_state: config.company_state || '',
        company_country: config.company_country || 'República Dominicana',
        company_email: config.company_email || '',
        company_website: config.company_website || '',
        company_phone: config.company_phone || '',
        currency: config.currency || 'DOP',
        rnc: config.rnc || '',
      });
    }
  }, [config]);

  // Efecto para mostrar error de consulta
  React.useEffect(() => {
    if (queryError) {
      console.error('Error al cargar configuración:', queryError);
      toast({
        title: "Error de Base de Datos",
        description: "No se pudo cargar la configuración. Asegúrate de que la tabla 'company_config' exista.",
        variant: "destructive",
      });
    }
  }, [queryError, toast]);

  // Mutación para guardar la configuración
  const saveConfigMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (config?.id) {
        // Actualizar registro existente
        const { error } = await supabase
          .from('company_config')
          .update(data)
          .eq('id', config.id);
        if (error) throw error;
      } else {
        // Crear nuevo registro
        const { error } = await supabase
          .from('company_config')
          .insert([data]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-config'] });
      toast({
        title: "Configuración guardada",
        description: "Los datos de la empresa se han actualizado correctamente.",
      });
    },
    onError: (error) => {
      console.error('Error al guardar configuración:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración. Inténtalo de nuevo.",
        variant: "destructive",
      });
    },
  });

  // Función para manejar la carga del logo
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Solo se permiten archivos de imagen.",
        variant: "destructive",
      });
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "El archivo es demasiado grande. Máximo 5MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingLight(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `company-logo-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Subir archivo
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      // Actualizar configuración con nueva URL del logo
      const updatedData = { ...formData, company_logo_url: publicUrl };
      
      if (config?.id) {
        const { error } = await supabase
          .from('company_config')
          .update({ company_logo_url: publicUrl })
          .eq('id', config.id);
        if (error) throw error;
      }

      queryClient.invalidateQueries({ queryKey: ['company-config'] });
      
      toast({
        title: "Logo actualizado",
        description: "El logo de la empresa se ha subido correctamente.",
      });

    } catch (error) {
      console.error('Error al subir logo:', error);
      toast({
        title: "Error",
        description: "No se pudo subir el logo. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingLight(false);
    }
  };



  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveConfigMutation.mutate(formData);
  };

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Acceso Denegado</h2>
            <p className="text-gray-600">Solo los administradores pueden acceder a esta sección.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (queryError) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error de Configuración</h2>
            <p className="text-gray-600 mb-4">
              La tabla de configuración de empresa no existe. Es necesario ejecutar la migración de base de datos.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Instrucciones:</strong><br />
                1. Ve al Dashboard de Supabase<br />
                2. Abre el SQL Editor<br />
                3. Ejecuta el archivo <code>manual_company_config_migration.sql</code>
              </p>
            </div>
            <Button onClick={() => window.location.reload()} variant="outline">
              Reintentar
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-full config-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Configuración de Empresa</h1>
          <p className="text-muted-foreground">
            Configura la información general de tu empresa y personaliza el logo.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-4">
          {/* Logo de la empresa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Logo de la Empresa
              </CardTitle>
              <CardDescription>
                Sube el logo de tu empresa.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo de la empresa */}
              <div className="space-y-3">
                {config?.company_logo_url && (
                  <div className="flex justify-center p-3 bg-white border rounded-lg">
                    <img
                      src={config.company_logo_url}
                      alt="Logo de la empresa"
                      className="max-h-20 max-w-full object-contain"
                    />
                  </div>
                )}
                
                <input
                  ref={lightLogoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => lightLogoInputRef.current?.click()}
                  disabled={isUploadingLight}
                >
                  {isUploadingLight ? (
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-3 w-3" />
                  )}
                  {config?.company_logo_url ? 'Cambiar' : 'Subir'}
                </Button>
              </div>

              {/* Mensaje informativo */}
              {config?.company_logo_url && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 Este logo se mostrará en toda la aplicación.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información de la empresa */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Información de la Empresa</CardTitle>
              <CardDescription>
                Completa los datos generales de tu empresa.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nombre / Razón Social *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rnc">RNC</Label>
                    <Input
                      id="rnc"
                      value={formData.rnc}
                      onChange={(e) => handleInputChange('rnc', e.target.value)}
                      placeholder="123456789"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_address">Dirección</Label>
                  <Textarea
                    id="company_address"
                    value={formData.company_address}
                    onChange={(e) => handleInputChange('company_address', e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="company_city">Ciudad</Label>
                    <Input
                      id="company_city"
                      value={formData.company_city}
                      onChange={(e) => handleInputChange('company_city', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_state">Estado/Provincia</Label>
                    <Input
                      id="company_state"
                      value={formData.company_state}
                      onChange={(e) => handleInputChange('company_state', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_country">País</Label>
                    <Input
                      id="company_country"
                      value={formData.company_country}
                      onChange={(e) => handleInputChange('company_country', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company_email">Correo Electrónico</Label>
                    <Input
                      id="company_email"
                      type="email"
                      value={formData.company_email}
                      onChange={(e) => handleInputChange('company_email', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company_phone">Teléfono</Label>
                    <Input
                      id="company_phone"
                      value={formData.company_phone}
                      onChange={(e) => handleInputChange('company_phone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company_website">Sitio Web</Label>
                    <Input
                      id="company_website"
                      type="url"
                      value={formData.company_website}
                      onChange={(e) => handleInputChange('company_website', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Moneda</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => handleInputChange('currency', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DOP">Peso Dominicano (DOP)</SelectItem>
                        <SelectItem value="USD">Dólar Americano (USD)</SelectItem>
                        <SelectItem value="EUR">Euro (EUR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="submit"
                    disabled={saveConfigMutation.isPending}
                    className="flex-1 sm:flex-none"
                  >
                    {saveConfigMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Guardar Configuración
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default CompanyConfig; 